import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, Upload, FileText, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const InstitutionalMembershipForm = () => {
  const [formData, setFormData] = useState({
    sansthaName: '',
    address: '',
    registrationNumber: '',
    numberOfFamilies: '',
    contactPhone: '',
    sansthaEmail: '',
    officeBarers: [
      { designation: 'अध्यक्ष / President', name: '', contact: '' },
      { designation: 'मंत्री / Secretary', name: '', contact: '' },
    ],
    hasConstitution: '',
    otherInfo: '',
    paymentAmount: '',
    paymentMode: '',
    paymentDate: '',
    transactionNumber: '',
    // Login credentials
    createLogin: false,
    loginEmail: '',
    loginPassword: '',
    confirmPassword: '',
  });

  const [constitutionFile, setConstitutionFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear file if user selects "No" for constitution
    if (name === 'hasConstitution' && value === 'no') {
      setConstitutionFile(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload PDF, DOC, DOCX, or image files only');
        return;
      }
      setConstitutionFile(file);
    }
  };

  const removeFile = () => {
    setConstitutionFile(null);
  };

  const handleOfficeBearerChange = (index, field, value) => {
    const newOfficeBarers = [...formData.officeBarers];
    newOfficeBarers[index][field] = value;
    setFormData({ ...formData, officeBarers: newOfficeBarers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.loginPassword !== formData.confirmPassword) {
      toast.error('पासवर्ड मेल नहीं खाता / Passwords do not match');
      return;
    }
    
    if (formData.loginPassword.length < 6) {
      toast.error('पासवर्ड कम से कम 6 अक्षर का होना चाहिए / Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      // Create FormData for multipart submission
      const submitData = new FormData();
      submitData.append('sansthaName', formData.sansthaName);
      submitData.append('address', formData.address);
      submitData.append('registrationNumber', formData.registrationNumber || '');
      submitData.append('numberOfFamilies', formData.numberOfFamilies || '');
      submitData.append('contactPhone', formData.contactPhone || '');
      submitData.append('sansthaEmail', formData.sansthaEmail || '');
      submitData.append('officeBarers', JSON.stringify(formData.officeBarers));
      submitData.append('hasConstitution', formData.hasConstitution || '');
      submitData.append('otherInfo', formData.otherInfo || '');
      submitData.append('paymentAmount', formData.paymentAmount || '');
      submitData.append('paymentMode', formData.paymentMode || '');
      submitData.append('paymentDate', formData.paymentDate || '');
      submitData.append('transactionNumber', formData.transactionNumber || '');
      // Login credentials
      submitData.append('loginEmail', formData.loginEmail);
      submitData.append('loginPassword', formData.loginPassword);
      
      // Append file if exists
      if (constitutionFile) {
        submitData.append('constitutionFile', constitutionFile);
      }

      const response = await axios.post(`${API_URL}/api/institutional-membership`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('पंजीकरण फॉर्म सफलतापूर्वक जमा किया गया! / Registration form submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('त्रुटि / Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 md:p-12 shadow-floating text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-serif font-bold text-3xl text-primary mb-4">
              पंजीकरण सफल! / Registration Successful!
            </h1>
            <p className="text-gray-600 mb-6">
              आपका संस्थागत सदस्यता आवेदन सफलतापूर्वक जमा हो गया है।
              <br />
              Your institutional membership application has been submitted successfully.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-orange-800">
                <strong>अगला कदम:</strong> महासभा प्रशासन आपके आवेदन की समीक्षा करेगा। 
                अनुमोदन के बाद आपको एक विशिष्ट संस्था पहचान संख्या (Institution ID) प्रदान की जाएगी।
              </p>
              <p className="text-orange-800 mt-2">
                <strong>Next Step:</strong> The Mahasabha administration will review your application. 
                Upon approval, you will be assigned a unique Institution ID.
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-primary hover:bg-primary/90"
            >
              मुख्य पृष्ठ पर वापस जाएं / Return to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8 md:p-12 shadow-floating">
          {/* Header */}
          <div className="text-center mb-12 border-b-2 border-secondary pb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-primary mb-3">
              महासभा संस्थागत सदस्यता पंजीकरण प्रपत्र
            </h1>
            <h2 className="font-serif font-bold text-2xl md:text-3xl text-secondary">
              MAHASABHA INSTITUTIONAL MEMBERSHIP REGISTRATION FORM
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* 1. Sanstha Name */}
            <div className="space-y-2">
              <Label htmlFor="sansthaName" className="text-base font-semibold">
                1. संस्था / पंचायत का नाम / Name of the Sanstha / Panchayat: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sansthaName"
                name="sansthaName"
                type="text"
                value={formData.sansthaName}
                onChange={handleChange}
                required
                className="text-base"
                data-testid="sanstha-name-input"
              />
            </div>

            {/* 2. Full Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="text-base font-semibold">
                2. पूरा पता / Full Address: <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                rows={3}
                className="text-base"
                data-testid="address-input"
              />
            </div>

            {/* 3. Registration Number */}
            <div className="space-y-2">
              <Label htmlFor="registrationNumber" className="text-base font-semibold">
                3. यदि किसी सरकारी निकाय में पंजीकृत है तो उसका पंजीकरण क्रमांक लिखें /<br />
                If registered with any Government body, mention Registration Number:
              </Label>
              <Input
                id="registrationNumber"
                name="registrationNumber"
                type="text"
                value={formData.registrationNumber}
                onChange={handleChange}
                className="text-base"
                data-testid="registration-number-input"
              />
            </div>

            {/* 4. Number of Families */}
            <div className="space-y-2">
              <Label htmlFor="numberOfFamilies" className="text-base font-semibold">
                4. पंचायत / संस्था के क्षेत्र में निवास करने वाले परिवारों की संख्या / सदस्यों / ट्रस्टियों की संख्या /<br />
                Number of Families residing in the area of the Panchayat / Sanstha / Number of Members / Trustees: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="numberOfFamilies"
                name="numberOfFamilies"
                type="number"
                value={formData.numberOfFamilies}
                onChange={handleChange}
                required
                className="text-base"
                data-testid="families-count-input"
              />
            </div>

            {/* 5. Contact Phone */}
            <div className="space-y-2">
              <Label htmlFor="contactPhone" className="text-base font-semibold">
                5. संपर्क दूरभाष / मोबाइल क्रमांक / Contact Phone / Mobile Number: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contactPhone"
                name="contactPhone"
                type="tel"
                value={formData.contactPhone}
                onChange={handleChange}
                required
                className="text-base"
                data-testid="contact-phone-input"
              />
            </div>

            {/* 6. Sanstha Email */}
            <div className="space-y-2">
              <Label htmlFor="sansthaEmail" className="text-base font-semibold">
                6. संस्था का ईमेल / Email ID of Sanstha: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sansthaEmail"
                name="sansthaEmail"
                type="email"
                value={formData.sansthaEmail}
                onChange={handleChange}
                required
                className="text-base"
                placeholder="example@email.com"
                data-testid="sanstha-email-input"
              />
            </div>

            {/* 7. Office Bearers */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">
                7. पदाधिकारियों के नाम (संपूर्ण नाम, नुख्स सहित) एवं संपर्क विवरण /<br />
                Details of Office Bearers (Full Name with Nukhs and Contact Numbers):
              </Label>
              <div className="overflow-x-auto">
                <table className="w-full border border-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border border-border p-2 text-left text-sm">
                        क्रमांक<br />Sr. No.
                      </th>
                      <th className="border border-border p-2 text-left text-sm">
                        पद<br />Designation
                      </th>
                      <th className="border border-border p-2 text-left text-sm">
                        नाम (संपूर्ण नाम व नुख्स सहित)<br />Full Name with Nukhs
                      </th>
                      <th className="border border-border p-2 text-left text-sm">
                        मोबाइल नंबर<br />Contact No.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.officeBarers.map((bearer, index) => (
                      <tr key={index}>
                        <td className="border border-border p-2 text-center">{index + 1}</td>
                        <td className="border border-border p-2 text-sm">{bearer.designation}</td>
                        <td className="border border-border p-2">
                          <Input
                            type="text"
                            value={bearer.name}
                            onChange={(e) => handleOfficeBearerChange(index, 'name', e.target.value)}
                            required={index < 2}
                            className="border-0 focus-visible:ring-0 text-sm"
                            data-testid={`bearer-name-${index}`}
                          />
                        </td>
                        <td className="border border-border p-2">
                          <Input
                            type="tel"
                            value={bearer.contact}
                            onChange={(e) => handleOfficeBearerChange(index, 'contact', e.target.value)}
                            required={index < 2}
                            className="border-0 focus-visible:ring-0 text-sm"
                            data-testid={`bearer-contact-${index}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 8. Constitution Availability */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                8. क्या संस्था / पंचायत का संविधान उपलब्ध है? /<br />
                Is a Constitution / By-laws of the Sanstha or Panchayat available? <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasConstitution"
                    value="yes"
                    checked={formData.hasConstitution === 'yes'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                    data-testid="constitution-yes"
                  />
                  <span>हाँ / Yes</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasConstitution"
                    value="no"
                    checked={formData.hasConstitution === 'no'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                    data-testid="constitution-no"
                  />
                  <span>नहीं / No</span>
                </label>
              </div>
              
              {/* File Upload - shown only when "Yes" is selected */}
              {formData.hasConstitution === 'yes' && (
                <div className="mt-4 p-4 border-2 border-dashed border-primary/30 rounded-lg bg-primary/5">
                  <Label className="text-sm font-medium text-primary mb-2 block">
                    संविधान / नियमावली की प्रति संलग्न करें / Attach a copy of Constitution / By-laws:
                  </Label>
                  
                  {!constitutionFile ? (
                    <div className="flex flex-col items-center justify-center py-4">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className="hidden"
                          data-testid="constitution-file-input"
                        />
                        <div className="flex flex-col items-center gap-2 p-4 border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors">
                          <Upload className="w-8 h-8 text-primary" />
                          <span className="text-sm text-primary font-medium">
                            फ़ाइल चुनें / Choose File
                          </span>
                          <span className="text-xs text-muted-foreground">
                            PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                          </span>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-green-800">{constitutionFile.name}</p>
                          <p className="text-xs text-green-600">
                            {(constitutionFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeFile}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {formData.hasConstitution === 'no' && (
                <p className="text-sm text-muted-foreground mt-2 italic">
                  कृपया संविधान / नियमावली तैयार करने पर विचार करें। / Please consider preparing a Constitution / By-laws.
                </p>
              )}
            </div>

            {/* 9. Other Information */}
            <div className="space-y-2">
              <Label htmlFor="otherInfo" className="text-base font-semibold">
                9. अन्य आवश्यक जानकारी (वैकल्पिक) / Other Relevant Information (Optional):
              </Label>
              <Textarea
                id="otherInfo"
                name="otherInfo"
                value={formData.otherInfo}
                onChange={handleChange}
                rows={3}
                className="text-base"
                data-testid="other-info-input"
              />
            </div>

            {/* 10. Payment Details */}
            <div className="space-y-4 bg-muted/30 p-6 rounded-lg">
              <Label className="text-base font-semibold block">
                10. पंजीकरण शुल्क राशि एवं भुगतान विवरण / Registration Fee Amount and Payment Details:
              </Label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentAmount" className="text-sm">
                    राशि / Amount (₹): <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="paymentAmount"
                    name="paymentAmount"
                    type="number"
                    value={formData.paymentAmount}
                    onChange={handleChange}
                    required
                    placeholder="100"
                    data-testid="payment-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">
                    भुगतान का माध्यम / Mode of Payment: <span className="text-destructive">*</span>
                  </Label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="upi"
                        checked={formData.paymentMode === 'upi'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="text-sm">UPI / QR Code</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="bank"
                        checked={formData.paymentMode === 'bank'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="text-sm">बैंक / Bank Transfer</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="cash"
                        checked={formData.paymentMode === 'cash'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="text-sm">नगद / Cash</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="other"
                        checked={formData.paymentMode === 'other'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="text-sm">अन्य / Other</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* UPI Payment Section */}
              {formData.paymentMode === 'upi' && (
                <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-3 text-center">
                    UPI से भुगतान करें / Pay via UPI
                  </h4>
                  <div className="flex flex-col items-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg"
                      alt="UPI QR Code - Scan to Pay"
                      className="max-w-xs w-full h-auto rounded-lg shadow-md mb-3"
                    />
                    <p className="text-sm text-blue-700 font-medium">
                      UPI ID: boim-661059350985@boi
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      किसी भी UPI ऐप से स्कैन करें / Scan with any UPI App
                    </p>
                  </div>
                </div>
              )}

              {/* Bank Transfer Details Section */}
              {formData.paymentMode === 'bank' && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-3">
                    बैंक विवरण / Bank Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="font-medium text-green-700">खाता नाम / Account Name:</span>
                        <p className="text-gray-800">BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">बैंक का नाम / Bank Name:</span>
                        <p className="text-gray-800">BANK OF INDIA</p>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">खाता संख्या / Account No:</span>
                        <p className="text-gray-800 font-mono">661020110000985</p>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">IFSC कोड / IFSC Code:</span>
                        <p className="text-gray-800 font-mono">BKID0006610</p>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">शाखा / Branch:</span>
                        <p className="text-gray-800">JODHPUR, RAJASTHAN</p>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">MICR कोड / MICR Code:</span>
                        <p className="text-gray-800 font-mono">342013002</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <span className="font-medium text-green-700">पता / Address:</span>
                      <p className="text-gray-800">GF 124 V2 SHOPPING PLAZA, NEW DELHI, INDIA - 110001</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentDate" className="text-sm">
                    भुगतान दिनांक / Date of Payment: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="paymentDate"
                    name="paymentDate"
                    type="date"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    required
                    data-testid="payment-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="transactionNumber" className="text-sm">
                    लेनदेन / रसीद क्रमांक / Transaction or Receipt No.:
                  </Label>
                  <Input
                    id="transactionNumber"
                    name="transactionNumber"
                    type="text"
                    value={formData.transactionNumber}
                    onChange={handleChange}
                    data-testid="transaction-number-input"
                  />
                </div>
              </div>
            </div>

            {/* 11. Login Credentials */}
            <div className="space-y-4 bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
              <Label className="text-base font-semibold block">
                11. लॉगिन विवरण / Login Credentials:
              </Label>
              <p className="text-sm text-muted-foreground">
                पंजीकरण के बाद पोर्टल में लॉगिन करने के लिए ईमेल और पासवर्ड बनाएं / Create email and password to login to the portal after registration
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="loginEmail" className="text-sm">
                    लॉगिन ईमेल / Login Email: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="loginEmail"
                    name="loginEmail"
                    type="email"
                    value={formData.loginEmail}
                    onChange={handleChange}
                    required
                    placeholder="login@example.com"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loginPassword" className="text-sm">
                    पासवर्ड / Password: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="loginPassword"
                    name="loginPassword"
                    type="password"
                    value={formData.loginPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="न्यूनतम 6 अक्षर / Minimum 6 characters"
                    data-testid="login-password-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="confirmPassword" className="text-sm">
                    पासवर्ड की पुष्टि करें / Confirm Password: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="पासवर्ड दोबारा दर्ज करें / Re-enter password"
                    data-testid="confirm-password-input"
                  />
                  {formData.loginPassword && formData.confirmPassword && formData.loginPassword !== formData.confirmPassword && (
                    <p className="text-destructive text-sm">पासवर्ड मेल नहीं खाता / Passwords do not match</p>
                  )}
                </div>
              </div>
            </div>

            {/* Declaration */}
            <div className="bg-primary/5 p-6 rounded-lg space-y-4 border-2 border-primary/20">
              <h3 className="font-serif font-bold text-xl text-primary text-center">
                घोषणा / DECLARATION
              </h3>
              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                <p>
                  हम, हस्ताक्षरकर्ता, यह घोषित करते हैं कि हम अपनी पंचायत / संस्था को महासभा के साथ पंजीकृत कराने हेतु आवेदन कर रहे हैं।
                  हम महासभा के संविधान में वर्णित सभी नियमों एवं शर्तों का पालन करेंगे तथा समाज के सर्वांगीण विकास के लिए महासभा की सामाजिक गतिविधियों का अंग बनकर कार्य करेंगे।
                </p>
                <p>
                  We, the undersigned, hereby declare that we are applying for registration of our Panchayat / Sanstha with the Mahasabha.
                  We agree to abide by all rules and conditions of the Mahasabha's Constitution and will actively participate in all social activities for the development and welfare of our community.
                </p>
              </div>
              <div className="pt-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    className="w-5 h-5 mt-1"
                    data-testid="declaration-checkbox"
                  />
                  <span className="text-sm text-foreground">
                    मैं / हम उपरोक्त घोषणा से सहमत हैं / I / We agree to the above declaration <span className="text-destructive">*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 text-white font-serif text-lg px-12 py-6 w-full md:w-auto"
                disabled={loading}
                data-testid="submit-institutional-form"
              >
                {loading ? 'जमा हो रहा है... / Submitting...' : 'फॉर्म जमा करें / Submit Form'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default InstitutionalMembershipForm;
